import * as React from "react";
import { LeftOutlined } from "@ant-design/icons";
import { Link, useNavigate } from "@tanstack/react-router";
import { Input, message, Modal, Radio, Skeleton, Typography } from "antd5";
import classnames from "classnames";
import { z } from "zod";

import { InfoButton } from "components/actions/InfoButton";
import TextButton from "components/actions/TextButton";
import Button from "components/ui/button/Button";
import {
  IntegrationSubscription,
  useIntegrationSubscriptions,
} from "lib/hooks/api/lead_subscriptions/useIntegrationSubscriptions";
import { pluralise } from "lib/utils";
import { useSupportChat } from "lib/utils/useSupportChat";
import SettingsContentContainer from "../SettingsContentContainer";
import NotificationEditor from "./NotificationEditor";

import css from "./IntegrationNotifications.module.scss";

const typeLabel = {
  TEAMS: "Teams",
  SLACK: "Slack",
};

// Full absolute paths for each integration type — used by both the list and the form.
const backRoute = {
  SLACK: "/account-management/notifications/slack" as const,
  TEAMS: "/account-management/notifications/teams" as const,
};

const createRoute = {
  SLACK: "/account-management/notifications/slack/create" as const,
  TEAMS: "/account-management/notifications/teams/create" as const,
};

const editRoute = {
  SLACK: "/account-management/notifications/slack/edit/$id" as const,
  TEAMS: "/account-management/notifications/teams/edit/$id" as const,
};

type IntegrationType = "TEAMS" | "SLACK";

// Mirrors app-service's webhookUrl schema, so the form explains a bad URL before trying to save it.
const webhookUrlSchema = z
  .url({
    protocol: /^https$/,
    error: "Enter the full webhook URL, starting with https://",
    abort: true,
  })
  .refine((url) => {
    const host = new URL(url).hostname;
    return host === "hooks.slack.com" || (host !== "slack.com" && !host.endsWith(".slack.com"));
  }, "That's a Slack page link, not a webhook URL. Slack webhook URLs start with https://hooks.slack.com/services/");

function pausedMessage(subscription: IntegrationSubscription, type: IntegrationType): string {
  if (subscription.pausedByRefusals) {
    return `Paused: ${typeLabel[type]} kept refusing these notifications. Update the webhook URL to resume.`;
  }
  return "Paused: these notifications aren't being sent.";
}

function webhookUrlProblem(url: string): string | undefined {
  return webhookUrlSchema.safeParse(url).error?.issues[0]?.message;
}

// Rendered by the index route — shows the list of existing subscriptions.
export function IntegrationIndex({ type }: { type: IntegrationType }): JSX.Element {
  return (
    <SettingsContentContainer>
      <h1>{typeLabel[type]} notifications</h1>
      <SubscriptionList type={type} />
    </SettingsContentContainer>
  );
}

type IntegrationNotificationsSettingsProps = {
  subscriptionId?: string;
  type: IntegrationType;
};

// Rendered by the create and edit routes.
export function IntegrationsNotificationSettings({
  subscriptionId,
  type,
}: IntegrationNotificationsSettingsProps) {
  const navigate = useNavigate();
  const { openSupportChat } = useSupportChat();
  const settings = useIntegrationSubscriptions();
  const subscriptionType = type.toLowerCase() as "slack" | "teams";

  const existingSubscription = React.useMemo((): IntegrationSubscription | undefined => {
    if (!subscriptionId) return;
    const existing = settings.companySubscriptions?.[subscriptionType].find(
      (i) => i.id === subscriptionId,
    );
    if (!existing && settings.status === "ready") {
      // TODO: Send sentry error? What do we want to do in this case?
      void message.error("We were not able to find this notification.");
    } else return existing;
  }, [settings.companySubscriptions, settings.status, subscriptionId, subscriptionType]);

  const [selectedViewIds, setSelectedViewIds] = React.useState<string[]>(
    existingSubscription ? existingSubscription.views.map((v) => v.id) : [],
  );

  React.useEffect(() => {
    if (existingSubscription) {
      setSelectedViewIds(existingSubscription.views.map((v) => v.id));
      setWebhookURL(existingSubscription.webhookUrl);
      setName(existingSubscription.name);
      setFrequency(existingSubscription.frequency);
    }
  }, [existingSubscription]);

  const [webhookURL, setWebhookURL] = React.useState<string | undefined>(
    existingSubscription ? existingSubscription.webhookUrl : undefined,
  );
  const [name, setName] = React.useState<string | undefined>(
    existingSubscription ? existingSubscription.name : undefined,
  );
  const [frequency, setFrequency] = React.useState<"DAILY" | "WEEKLY">(
    existingSubscription ? existingSubscription.frequency : "DAILY",
  );

  const handleUpdate = React.useCallback(async () => {
    if (!existingSubscription) return;
    try {
      await settings.updateSubscription(
        {
          webhookUrl: webhookURL,
          name: name,
          viewIds: selectedViewIds,
          frequency: frequency,
        },
        existingSubscription.id,
      );
      void message.success("Changes saved");
      void navigate({ to: backRoute[type] });
    } catch {
      void message.error("Issue saving the notification");
    }
  }, [
    name,
    selectedViewIds,
    existingSubscription,
    frequency,
    settings,
    webhookURL,
    type,
    navigate,
  ]);

  const handleCreate = React.useCallback(async () => {
    try {
      await settings.createSubscription({
        webhookUrl: webhookURL,
        name: name,
        viewIds: selectedViewIds,
        frequency: frequency,
        type: type,
      });
      void message.success("Notification successfully created");
      void navigate({ to: backRoute[type] });
    } catch {
      void message.error("Issue saving the notification");
    }
  }, [settings, name, selectedViewIds, frequency, webhookURL, type, navigate]);

  const urlProblem = webhookURL ? webhookUrlProblem(webhookURL) : undefined;

  // TODO handle the form differently. Didn't want to use ant forms though
  const validForm = React.useMemo(() => {
    if (!webhookURL || !name || urlProblem) return false;
    return true;
  }, [webhookURL, name, urlProblem]);

  return (
    <SettingsContentContainer className={css.integrationsNotifications}>
      {" "}
      <Link to={backRoute[type]}>
        <LeftOutlined />
        {typeLabel[type]} Notifications
      </Link>
      <h1>
        {existingSubscription ? `Edit "${existingSubscription.name}"` : "Create a new notification"}
      </h1>
      {settings.status === "ready" ? (
        <>
          <div className={css.formItem}>
            <label className={css.label} htmlFor="subscription-name">
              Name:
            </label>
            <Input
              id="subscription-name"
              placeholder="Enter a name for this notification"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className={css.formItem}>
            <label className={css.label} htmlFor="subscription-webhook-url">
              Webhook URL:{" "}
              <InfoButton
                note={
                  <p>
                    Unsure of what a <i>Webhook URL</i> is? Please{" "}
                    <TextButton onClick={() => openSupportChat("Export button tooltip")}>
                      get in touch
                    </TextButton>{" "}
                    to find out more.
                  </p>
                }
                tooltipMaxWidth={400}
              />
            </label>
            <Input
              id="subscription-webhook-url"
              placeholder="Enter your URL here"
              value={webhookURL}
              status={urlProblem ? "error" : undefined}
              onChange={(e) => setWebhookURL(e.target.value.trim())}
            />
            {urlProblem && <Typography.Text type="danger">{urlProblem}</Typography.Text>}
          </div>
          <div className={classnames(css.formItem, css.inlineFormItem)}>
            <label className={css.label} htmlFor="subscription-frequency">
              Frequency:
            </label>
            <Radio.Group
              onChange={(e) => setFrequency(e.target.value)}
              value={frequency}
              id="subscription-frequency"
            >
              <Radio.Button value="DAILY">Daily</Radio.Button>
              <Radio.Button value="WEEKLY">Weekly</Radio.Button>
            </Radio.Group>
          </div>
        </>
      ) : (
        <Skeleton active={settings.status === "loading"} paragraph={{ rows: 2, width: "100%" }} />
      )}
      <NotificationEditor selectedViewIds={selectedViewIds} onViewsChange={setSelectedViewIds} />
      <div className={css.buttonContainer}>
        <Link to={backRoute[type]}>
          <Button buttonType="secondary">Cancel</Button>
        </Link>
        <Button
          disabled={!(settings.status === "ready" && validForm)}
          onClick={existingSubscription ? handleUpdate : handleCreate}
        >
          Save
        </Button>
      </div>
    </SettingsContentContainer>
  );
}

type DeleteModalProps = {
  isOpen: boolean;
  onClose: () => void;
  subscription: IntegrationSubscription;
  handleDelete: (id: string) => Promise<void>;
};

const DeleteModal = ({ subscription, isOpen, onClose, handleDelete }: DeleteModalProps) => (
  <Modal
    title="Delete notification?"
    okButtonProps={{
      type: "primary",
      children: "Delete",
      danger: true,
    }}
    onOk={() => {
      void handleDelete(subscription.id);
      onClose();
    }}
    open={isOpen}
    onCancel={onClose}
  >
    Would you like to delete "{subscription.name}"?
  </Modal>
);

function SubscriptionList({ type }: { type: IntegrationType }): JSX.Element {
  const settings = useIntegrationSubscriptions();
  const navigate = useNavigate();
  const [subscriptionToDelete, setSubscriptionToDelete] =
    React.useState<IntegrationSubscription | null>(null);

  const handleDelete = React.useCallback(
    async (id: string) => {
      try {
        await settings.deleteSubscription(id);
        void message.success("Successfully deleted notification");
      } catch {
        void message.error("There was an issue deleting this notification.");
      }
    },
    [settings],
  );

  if (settings.status === "ready") {
    const subscriptionType = type.toLowerCase() as "teams" | "slack";
    const subscriptions = settings.companySubscriptions?.[subscriptionType] ?? [];

    return (
      <>
        {subscriptions.length ? (
          subscriptions.map((s) => {
            const views = s.views.map((v) => v.name);

            return (
              <div
                key={s.id}
                className={css.subscriptionSelector}
                onClick={() => void navigate({ to: editRoute[type], params: { id: s.id } })}
              >
                <h2>{s.name}</h2>
                {s.disabled && (
                  <Typography.Text type="warning">{pausedMessage(s, type)}</Typography.Text>
                )}
                {/* stopPropagation so the card click doesn't fire when using Edit/Delete */}
                <Link
                  to={editRoute[type]}
                  params={{ id: s.id }}
                  className={css.edit}
                  onClick={(e) => e.stopPropagation()}
                >
                  Edit
                </Link>
                <TextButton
                  danger
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setSubscriptionToDelete(s);
                  }}
                  className={css.deleteIcon}
                >
                  Delete
                </TextButton>
                <div className={css.listsAndViews}>
                  <p>
                    <b>{`${pluralise(views.length, "View")}${views.length ? ":" : ""}`}</b>{" "}
                    {views.join(", ")}
                  </p>
                </div>
                <div>
                  <b>Frequency:</b> {s.frequency.toLowerCase()}
                </div>
              </div>
            );
          })
        ) : (
          <div className={css.noSubscriptionsMessage}>
            You don't currently have any {typeLabel[type]} notifications set up.
          </div>
        )}
        <div>
          <Link to={createRoute[type]}>
            <Button icon="plus">Create a new notification</Button>
          </Link>
        </div>
        {subscriptionToDelete && (
          <DeleteModal
            isOpen
            onClose={() => setSubscriptionToDelete(null)}
            subscription={subscriptionToDelete}
            handleDelete={handleDelete}
          />
        )}
      </>
    );
  }

  return <Skeleton active={settings.status === "loading"} />;
}
